import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    IconButton,
    LinearProgress,
    Pagination,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import BugReportOutlinedIcon from "@mui/icons-material/BugReportOutlined";
import { fetchActiveAdminUploads } from "@/Service/AdminUploadMonitorService";
import { toDate } from "@/utils/adminDates";
import { PageWrapper } from "./SystemAlertPage.elements";
import AdminUploadsStatsSection from "./AdminUploadsStatsSection";
import AdminUploadDiagnosticsDrawer from "./AdminUploadDiagnosticsDrawer";
import {
    HEALTH_LABEL,
    HEALTH_SEVERITY,
    deriveRowHealth,
} from "./adminUploadHealth";

// Active-only chip set (matches backend UploadStatus.ACTIVE_STATUSES). The
// admin can toggle out of this into the full-history set via "Show all"; the
// "Active only" button snaps back here.
const ACTIVE_STATUSES = [
    "PENDING_CHUNKS",
    "CHUNK_UPLOAD_IN_PROGRESS",
    "PAUSED",
    "CHUNK_UPLOAD_ASSEMBLING",
    "READY_TO_UPLOAD_TO_PROVIDER",
    "IN_PROGRESS_UPLOAD_TO_PROVIDER",
    "READY_FOR_CAT_DV",
];

// Terminal / post-transfer statuses. Grouped separately so they render in a
// second chip row and are visually distinguishable from live-transfer ones.
// Keep in sync with backend UploadStatus.java.
const TERMINAL_STATUSES = [
    "CAT_DV_INGESTED",
    "UPLOADED",
    "EDIT_IN_PROGRESS",
    "EDITED",
    "TAGGING_IN_PROGRESS",
    "TAGGED",
    "PUBLISHED",
    "ON_HOLD",
    "REJECTED",
    "FAILED",
    "CANCELLED",
];

const ALL_STATUSES = [...ACTIVE_STATUSES, ...TERMINAL_STATUSES];

const DEFAULT_STATUS_FILTER = [
    "PENDING_CHUNKS",
    "CHUNK_UPLOAD_IN_PROGRESS",
    "PAUSED",
    "CHUNK_UPLOAD_ASSEMBLING",
];

const STATUS_LABEL = {
    PENDING_CHUNKS: "Pending",
    CHUNK_UPLOAD_IN_PROGRESS: "Uploading",
    PAUSED: "Paused",
    CHUNK_UPLOAD_ASSEMBLING: "Assembling",
    READY_TO_UPLOAD_TO_PROVIDER: "Ready to Provider",
    IN_PROGRESS_UPLOAD_TO_PROVIDER: "Provider",
    READY_FOR_CAT_DV: "Ready to CatDV",
    CAT_DV_INGESTED: "CatDV ingested",
    UPLOADED: "Uploaded",
    EDIT_IN_PROGRESS: "Editing",
    EDITED: "Edited",
    TAGGING_IN_PROGRESS: "Tagging",
    TAGGED: "Tagged",
    PUBLISHED: "Published",
    ON_HOLD: "On hold",
    REJECTED: "Rejected",
    FAILED: "Failed",
    CANCELLED: "Cancelled",
};

const PAGE_SIZE = 50;
const POLL_INTERVAL_MS = 5000;

const formatBytes = (bytes) => {
    if (bytes == null || bytes <= 0) return "0 B";
    if (bytes < 1024) return `${bytes} B`;

    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;

    const mb = kb / 1024;
    if (mb < 1024) return `${mb.toFixed(1)} MB`;

    return `${(mb / 1024).toFixed(2)} GB`;
};

const formatRelative = (value) => {
    const date = toDate(value);
    if (!date) return "—";

    const seconds = Math.max(0, Math.round((Date.now() - date.getTime()) / 1000));
    if (seconds < 60) return `${seconds}s ago`;

    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;

    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ${minutes % 60}m ago`;

    const days = Math.floor(hours / 24);
    return `${days}d ${hours % 24}h ago`;
};

const formatTimestamp = (value) => {
    const date = toDate(value);
    return date ? date.toLocaleString() : "";
};

const STATUS_COLOR = (theme) => ({
    PENDING_CHUNKS: theme.palette.grey[500],
    CHUNK_UPLOAD_IN_PROGRESS: theme.palette.primary.light,
    PAUSED: theme.palette.warning.main,
    CHUNK_UPLOAD_ASSEMBLING: theme.palette.primary.main,
    READY_TO_UPLOAD_TO_PROVIDER: theme.palette.primary.main,
    IN_PROGRESS_UPLOAD_TO_PROVIDER: theme.palette.primary.light,
    READY_FOR_CAT_DV: theme.palette.success.light,
    CAT_DV_INGESTED: theme.palette.success.main,
    UPLOADED: theme.palette.success.main,
    EDIT_IN_PROGRESS: theme.palette.info.main,
    EDITED: theme.palette.info.dark,
    TAGGING_IN_PROGRESS: theme.palette.info.main,
    TAGGED: theme.palette.info.dark,
    PUBLISHED: theme.palette.success.dark,
    // Amber, matching PAUSED above: both mean deliberately stopped.
    ON_HOLD: theme.palette.warning.main,
    REJECTED: theme.palette.error.main,
    FAILED: theme.palette.error.main,
    CANCELLED: theme.palette.grey[600],
});

const hiddenScrollbarSx = {
    scrollbarWidth: "none",
    "&::-webkit-scrollbar": {
        display: "none",
    },
};

const AdminUploadsLivePage = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const statusColors = useMemo(() => STATUS_COLOR(theme), [theme]);

    const [items, setItems] = useState([]);
    const [aggregates, setAggregates] = useState(null);
    const [totalElements, setTotalElements] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [page, setPage] = useState(0);
    const [statusFilter, setStatusFilter] = useState(DEFAULT_STATUS_FILTER);
    const [contributorQuery, setContributorQuery] = useState("");
    const [debouncedContributor, setDebouncedContributor] = useState("");
    // Package name / date range search. packageName debounces same as
    // contributor (300 ms) so the 5-s live poll doesn't fire a request on
    // every keystroke. Dates fire immediately — they change via a picker,
    // not a stream of characters.
    const [packageNameQuery, setPackageNameQuery] = useState("");
    const [debouncedPackageName, setDebouncedPackageName] = useState("");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(null);

    const prevBytesRef = useRef(new Map());
    // Upload Observability Platform: id of the row whose Diagnostics drawer is
    // open. null when closed. Row payload passed as `initialRow` so the drawer
    // can render immediately while the /diagnostics fetch is in flight.
    const [diagnosticsUploadId, setDiagnosticsUploadId] = useState(null);

    const chipBg = "rgba(255,255,255,0.06)";
    const chipBorder = "rgba(255,255,255,0.12)";
    const panelBg = "rgba(20,27,40,0.74)";
    const tileBg = "rgba(41,56,77,0.64)";

    useEffect(() => {
        const handle = setTimeout(() => setDebouncedContributor(contributorQuery), 300);
        return () => clearTimeout(handle);
    }, [contributorQuery]);

    useEffect(() => {
        const handle = setTimeout(() => setDebouncedPackageName(packageNameQuery), 300);
        return () => clearTimeout(handle);
    }, [packageNameQuery]);

    const fetchData = useCallback(
        async ({ silent } = {}) => {
            if (silent) setRefreshing(true);
            else setLoading(true);

            try {
                const data = await fetchActiveAdminUploads({
                    status: statusFilter,
                    contributor: debouncedContributor,
                    packageName: debouncedPackageName,
                    from: fromDate,
                    to: toDate,
                    page,
                    size: PAGE_SIZE,
                });

                const nextBytes = new Map();
                const enriched = (data.items || []).map((row) => {
                    const prev = prevBytesRef.current.get(row.uploadId);
                    const deltaBytes =
                        prev != null && row.bytesUploaded != null
                            ? Math.max(0, row.bytesUploaded - prev)
                            : 0;

                    nextBytes.set(row.uploadId, row.bytesUploaded ?? 0);

                    const throughputBps = deltaBytes / (POLL_INTERVAL_MS / 1000);

                    return { ...row, throughputBps };
                });

                prevBytesRef.current = nextBytes;

                setItems(enriched);
                setAggregates(data.aggregates || null);
                setTotalElements(data.totalElements || 0);
                setTotalPages(data.totalPages || 0);
                setLastUpdated(new Date());
                setError(null);
            } catch (e) {
                setError(e?.response?.data?.message || e?.message || "Failed to load uploads");
            } finally {
                setLoading(false);
                setRefreshing(false);
            }
        },
        [statusFilter, debouncedContributor, debouncedPackageName, fromDate, toDate, page]
    );

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        const tick = () => {
            if (document.hidden) return;
            fetchData({ silent: true });
        };

        const id = setInterval(tick, POLL_INTERVAL_MS);
        return () => clearInterval(id);
    }, [fetchData]);

    const toggleStatusFilter = (status) => {
        setPage(0);
        setStatusFilter((prev) =>
            prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]
        );
    };

    const handleManualRefresh = () => fetchData({ silent: true });

    return (
        <PageWrapper>
            <Stack
                spacing={{ xs: 2.25, md: 3.25 }}
                sx={{
                    width: "100%",
                    minWidth: 0,
                    pb: { xs: 2, md: 3 },
                }}
            >
                <PlainHeader
                    lastUpdated={lastUpdated}
                    loading={loading}
                    refreshing={refreshing}
                    onRefresh={handleManualRefresh}
                />

                <StatsSectionShell>
                    <AdminUploadsStatsSection />
                </StatsSectionShell>

                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "repeat(2, minmax(0, 1fr))",
                            sm: "repeat(2, minmax(0, 1fr))",
                            lg: "repeat(4, minmax(0, 1fr))",
                        },
                        gap: { xs: "10px", sm: "14px", md: "16px" },
                        width: "100%",
                        minWidth: 0,
                    }}
                >
                    <CounterTile label="Active" value={aggregates?.activeCount ?? "—"} background={tileBg} />
                    <CounterTile
                        label="Bytes uploaded"
                        value={formatBytes(aggregates?.totalBytesUploaded ?? 0)}
                        background={tileBg}
                    />
                    <CounterTile
                        label="Bytes in flight"
                        value={formatBytes(aggregates?.totalBytesInFlight ?? 0)}
                        hint="Sum of expected sizes across active uploads"
                        background={tileBg}
                    />
                    <CounterTile
                        label="Contributors"
                        value={aggregates?.uniqueContributors ?? "—"}
                        background={tileBg}
                    />
                </Box>

                <FiltersPanel
                    activeStatuses={ACTIVE_STATUSES}
                    terminalStatuses={TERMINAL_STATUSES}
                    statusFilter={statusFilter}
                    aggregates={aggregates}
                    statusColors={statusColors}
                    contributorQuery={contributorQuery}
                    setContributorQuery={setContributorQuery}
                    packageNameQuery={packageNameQuery}
                    setPackageNameQuery={setPackageNameQuery}
                    fromDate={fromDate}
                    setFromDate={setFromDate}
                    toDate={toDate}
                    setToDate={setToDate}
                    onClearFilters={() => {
                        setPage(0);
                        setContributorQuery("");
                        setPackageNameQuery("");
                        setFromDate("");
                        setToDate("");
                    }}
                    hasActiveFilters={
                        !!(contributorQuery || packageNameQuery || fromDate || toDate)
                    }
                    setPage={setPage}
                    toggleStatusFilter={toggleStatusFilter}
                    onSelectAllStatuses={() => {
                        setPage(0);
                        setStatusFilter(ALL_STATUSES);
                    }}
                    onResetToActive={() => {
                        setPage(0);
                        setStatusFilter(DEFAULT_STATUS_FILTER);
                    }}
                    chipBg={chipBg}
                    chipBorder={chipBorder}
                    panelBg={panelBg}
                    theme={theme}
                />

                {error && (
                    <Alert
                        severity="error"
                        sx={{
                            borderRadius: "14px",
                            background: "rgba(211, 47, 47, 0.12)",
                            color: "#fff",
                            border: "1px solid rgba(244, 67, 54, 0.32)",
                            px: { xs: 1.5, sm: 2 },
                            py: { xs: 1, sm: 1.25 },
                            "& .MuiAlert-icon": {
                                color: "#ff8a80",
                            },
                        }}
                    >
                        {error}
                    </Alert>
                )}

                {isMobile ? (
                    <MobileUploadsList
                        items={items}
                        loading={loading}
                        chipBg={chipBg}
                        chipBorder={chipBorder}
                        statusColors={statusColors}
                        onOpenDiagnostics={setDiagnosticsUploadId}
                    />
                ) : (
                    <DesktopUploadsTable
                        items={items}
                        loading={loading}
                        chipBg={chipBg}
                        chipBorder={chipBorder}
                        statusColors={statusColors}
                        onOpenDiagnostics={setDiagnosticsUploadId}
                    />
                )}

                <AdminUploadDiagnosticsDrawer
                    open={!!diagnosticsUploadId}
                    uploadId={diagnosticsUploadId}
                    initialRow={items.find((r) => r.uploadId === diagnosticsUploadId)}
                    onClose={() => setDiagnosticsUploadId(null)}
                />

                {totalPages > 1 && (
                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        justifyContent="space-between"
                        alignItems={{ xs: "stretch", sm: "center" }}
                        spacing={2}
                        sx={{ pt: { xs: 0.5, md: 0.25 } }}
                    >
                        <Typography
                            variant="caption"
                            sx={{
                                color: "#f7fafa80",
                                textAlign: { xs: "center", sm: "left" },
                            }}
                        >
                            Showing {items.length} of {totalElements}
                        </Typography>

                        <Pagination
                            count={totalPages}
                            page={page + 1}
                            onChange={(_e, value) => setPage(value - 1)}
                            size="small"
                            siblingCount={isMobile ? 0 : 1}
                            boundaryCount={1}
                            sx={{
                                display: "flex",
                                justifyContent: { xs: "center", sm: "flex-end" },
                                "& .MuiPaginationItem-root": {
                                    color: "#f7fafa",
                                    borderColor: "rgba(255,255,255,0.12)",
                                },
                                "& .Mui-selected": {
                                    background: `${theme.palette.primary.main} !important`,
                                },
                            }}
                        />
                    </Stack>
                )}
            </Stack>
        </PageWrapper>
    );
};

const PlainHeader = ({ lastUpdated, loading, refreshing, onRefresh }) => (
    <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        spacing={2}
        sx={{
            width: "100%",
            minWidth: 0,
            px: { xs: 0.25, sm: 0 },
            pt: { xs: 0.25, md: 0 },
            pb: { xs: 0.5, md: 0.75 },
        }}
    >
        <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
                variant="h5"
                sx={{
                    color: "#fff",
                    fontWeight: 800,
                    fontSize: { xs: "22px", sm: "24px" },
                    lineHeight: 1.05,
                    letterSpacing: "-0.03em",
                }}
            >
                Live Uploads
            </Typography>

            <Typography
                variant="body2"
                sx={{
                    color: "#f7fafaa6",
                    mt: 0.7,
                    maxWidth: 680,
                    fontSize: { xs: "12.5px", sm: "14px" },
                    lineHeight: { xs: 1.35, sm: 1.45 },
                }}
            >
                Real-time view of in-flight uploads across the system. Refreshes every 5s.
            </Typography>

            {lastUpdated && (
                <Typography
                    variant="caption"
                    sx={{
                        display: "block",
                        color: "#f7fafa73",
                        mt: 1.15,
                        fontSize: "11.5px",
                    }}
                >
                    Updated {formatRelative(lastUpdated)}
                </Typography>
            )}
        </Box>

        <Tooltip title="Refresh now">
            <IconButton
                size="small"
                onClick={onRefresh}
                disabled={loading}
                sx={{
                    color: "#f7fafa",
                    width: { xs: 34, sm: 36 },
                    height: { xs: 34, sm: 36 },
                    mt: { xs: 0.2, sm: 0 },
                    border: "1px solid rgba(255,255,255,0.14)",
                    background: "rgba(255,255,255,0.06)",
                    flexShrink: 0,
                    "&:hover": {
                        background: "rgba(255,255,255,0.12)",
                        borderColor: "rgba(255,255,255,0.24)",
                    },
                    "&.Mui-disabled": {
                        color: "rgba(255,255,255,0.45)",
                    },
                }}
            >
                {refreshing ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon fontSize="small" />}
            </IconButton>
        </Tooltip>
    </Stack>
);

const StatsSectionShell = ({ children }) => (
    <Box
        className="admin-uploads-stats-shell"
        sx={{
            width: "100%",
            minWidth: 0,
            overflow: "hidden",

            "& *": {
                boxSizing: "border-box",
            },

            "& .recharts-wrapper": {
                maxWidth: "100%",
            },

            "& .recharts-surface": {
                overflow: "visible",
            },

            "& .recharts-cartesian-axis-tick-value": {
                fill: "#f7fafaa6",
                fontSize: 12,
            },

            "& .recharts-label": {
                fill: "#f7fafaa6",
            },

            "@media (max-width: 899px)": {
                "&": {
                    overflowX: "hidden",
                },

                "& > *": {
                    minWidth: "0 !important",
                    maxWidth: "100% !important",
                },

                "& .recharts-responsive-container": {
                    width: "100% !important",
                    minWidth: "0 !important",
                    minHeight: "300px !important",
                    height: "auto !important",
                },

                "& .recharts-wrapper": {
                    width: "100% !important",
                    height: "auto !important",
                    minHeight: "300px !important",
                    display: "flex !important",
                    flexDirection: "column !important",
                    alignItems: "stretch !important",
                    justifyContent: "flex-start !important",
                    overflow: "visible !important",
                },

                "& .recharts-wrapper > .recharts-legend-wrapper": {
                    position: "static !important",
                    top: "auto !important",
                    right: "auto !important",
                    bottom: "auto !important",
                    left: "auto !important",
                    width: "100% !important",
                    maxWidth: "100% !important",
                    height: "auto !important",
                    transform: "none !important",
                    order: "0 !important",
                    margin: "2px 0 10px 0 !important",
                    padding: "0 2px !important",
                    lineHeight: "1.2 !important",
                },

                "& .recharts-wrapper > .recharts-surface": {
                    order: "1 !important",
                    width: "100% !important",
                    height: "220px !important",
                    minHeight: "220px !important",
                    maxHeight: "220px !important",
                    overflow: "visible !important",
                },

                "& .recharts-default-legend": {
                    display: "flex !important",
                    flexWrap: "wrap !important",
                    alignItems: "center !important",
                    justifyContent: "flex-start !important",
                    gap: "7px 10px !important",
                    width: "100% !important",
                    maxWidth: "100% !important",
                    margin: "0 !important",
                    padding: "0 !important",
                },

                "& .recharts-legend-item": {
                    display: "inline-flex !important",
                    alignItems: "center !important",
                    maxWidth: "100% !important",
                    marginRight: "0 !important",
                    whiteSpace: "nowrap !important",
                },

                "& .recharts-legend-item-text": {
                    color: "#f7fafa !important",
                    fontSize: "10.5px !important",
                    lineHeight: "1.2 !important",
                    whiteSpace: "nowrap !important",
                },

                "& .recharts-legend-icon": {
                    flexShrink: 0,
                },

                "& .recharts-pie-label-text, & .recharts-pie-label-line": {
                    display: "none !important",
                },

                "& .recharts-cartesian-grid-horizontal line, & .recharts-cartesian-grid-vertical line": {
                    opacity: 0.35,
                },

                "& .MuiTableContainer-root": {
                    overflowX: "visible !important",
                    width: "100% !important",
                },

                "& .MuiTable-root": {
                    display: "block !important",
                    width: "100% !important",
                    minWidth: "0 !important",
                },

                "& .MuiTableHead-root": {
                    display: "none !important",
                },

                "& .MuiTableBody-root": {
                    display: "grid !important",
                    gridTemplateColumns: "1fr !important",
                    gap: "10px !important",
                    width: "100% !important",
                },

                "& .MuiTableRow-root": {
                    display: "grid !important",
                    gridTemplateColumns: "repeat(2, minmax(0, 1fr)) !important",
                    gap: "10px !important",
                    width: "100% !important",
                    padding: "12px !important",
                    border: "1px solid rgba(255,255,255,0.09) !important",
                    borderRadius: "12px !important",
                    background: "rgba(255,255,255,0.035) !important",
                },

                "& .MuiTableCell-root": {
                    display: "flex !important",
                    alignItems: "center !important",
                    justifyContent: "space-between !important",
                    minWidth: "0 !important",
                    width: "100% !important",
                    padding: "0 !important",
                    borderBottom: "0 !important",
                    color: "#f7fafa !important",
                    fontSize: "12px !important",
                    lineHeight: "1.25 !important",
                    textAlign: "left !important",
                    gap: "10px !important",
                },

                "& .MuiTableCell-root:first-of-type": {
                    display: "block !important",
                    gridColumn: "1 / -1 !important",
                    paddingBottom: "10px !important",
                    borderBottom: "1px solid rgba(255,255,255,0.08) !important",
                },

                "& .MuiTableCell-root:nth-of-type(n + 2)": {
                    minHeight: "38px !important",
                    padding: "9px 10px !important",
                    borderRadius: "10px !important",
                    background: "rgba(255,255,255,0.04) !important",
                },

                "& .MuiTableCell-root:nth-of-type(2)::before": {
                    content: "'Successful'",
                    color: "#f7fafa80",
                    fontSize: "9px",
                    fontWeight: 800,
                    letterSpacing: "0.045em",
                    textTransform: "uppercase",
                },

                "& .MuiTableCell-root:nth-of-type(3)::before": {
                    content: "'Share'",
                    color: "#f7fafa80",
                    fontSize: "9px",
                    fontWeight: 800,
                    letterSpacing: "0.045em",
                    textTransform: "uppercase",
                },

                "& .MuiTableCell-root:nth-of-type(4)::before": {
                    content: "'Failed'",
                    color: "#f7fafa80",
                    fontSize: "9px",
                    fontWeight: 800,
                    letterSpacing: "0.045em",
                    textTransform: "uppercase",
                },

                "& .MuiTableCell-root:nth-of-type(5)::before": {
                    content: "'Total'",
                    color: "#f7fafa80",
                    fontSize: "9px",
                    fontWeight: 800,
                    letterSpacing: "0.045em",
                    textTransform: "uppercase",
                },
            },

            "@media (min-width: 900px)": {
                "& .recharts-wrapper": {
                    marginTop: "10px",
                },

                "& .recharts-legend-wrapper": {
                    marginBottom: "18px !important",
                },
            },
        }}
    >
        {children}
    </Box>
);

const searchFieldSx = (theme) => ({
    "& .MuiOutlinedInput-root": {
        background: "rgba(255,255,255,0.045)",
        borderRadius: "11px",
        color: "#f7fafa",
        // iOS Safari auto-zooms the entire page in whenever a focused input
        // has font-size < 16px — a big "not resizable" feeling on iPhone
        // because you tap a filter and the whole admin dashboard blows up.
        // Bumping to 16px on xs kills the auto-zoom; desktop shrinks back to
        // 13px so the filter panel stays visually compact.
        fontSize: { xs: 16, sm: "13px" },
        minHeight: 42,
        "& fieldset": { borderColor: "rgba(255,255,255,0.16)" },
        "&:hover fieldset": { borderColor: "rgba(255,255,255,0.28)" },
        "&.Mui-focused fieldset": { borderColor: theme.palette.primary.light },
    },
    "& .MuiInputBase-input": {
        // Match the outer font-size so the caret position + native selection
        // ring on iOS aren't off by a few px relative to the outlined border.
        fontSize: { xs: 16, sm: "13px" },
    },
    "& .MuiInputLabel-root": { color: "#f7fafaa6", fontSize: "13px" },
    "& .MuiInputLabel-root.Mui-focused": { color: theme.palette.primary.light },
    "& .MuiInputBase-input::placeholder": {
        color: "#f7fafa66",
        opacity: 1,
    },
    // datetime-local inputs use a browser-chrome calendar icon that renders
    // black-on-black inside our dark input; invert() flips it so it reads on
    // the dark background without shipping a custom SVG.
    "& input[type='datetime-local']::-webkit-calendar-picker-indicator": {
        filter: "invert(1)",
        opacity: 0.7,
        cursor: "pointer",
    },
    // iOS Safari reserves extra vertical space for datetime-local's native
    // spinner overlay AND ignores line-height, so the value can clip inside
    // a 42px min-height cell. Give it its own baseline so it doesn't overflow
    // the fieldset border on iPhone.
    "& input[type='datetime-local']": {
        minHeight: { xs: 24, sm: "auto" },
        lineHeight: 1.2,
        // WebKit-only reset of the default alignment iOS applies to native
        // date inputs (they left-align text but reserve right-side space for
        // the picker button, throwing off the placeholder centering).
        WebkitAppearance: "none",
        appearance: "none",
    },
});

const FiltersPanel = ({
                          activeStatuses,
                          terminalStatuses,
                          statusFilter,
                          aggregates,
                          statusColors,
                          contributorQuery,
                          setContributorQuery,
                          packageNameQuery,
                          setPackageNameQuery,
                          fromDate,
                          setFromDate,
                          toDate,
                          setToDate,
                          onClearFilters,
                          hasActiveFilters,
                          setPage,
                          toggleStatusFilter,
                          onSelectAllStatuses,
                          onResetToActive,
                          chipBg,
                          chipBorder,
                          panelBg,
                          theme,
                      }) => (
    <Box
        sx={{
            border: `1px solid ${chipBorder}`,
            borderRadius: { xs: "14px", md: "16px" },
            background: panelBg,
            px: { xs: 1.6, sm: 2, md: 2.25 },
            py: { xs: 1.6, sm: 1.9, md: 2.1 },
            minWidth: 0,
        }}
    >
        <Stack
            direction={{ xs: "column", lg: "row" }}
            spacing={{ xs: 1.75, lg: 2.5 }}
            alignItems={{ xs: "stretch", lg: "flex-end" }}
            justifyContent="space-between"
        >
            <Box sx={{ minWidth: 0, flex: 1 }}>
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ mb: 1.25 }}
                >
                    <Typography
                        variant="caption"
                        sx={{
                            color: "#f7fafaa6",
                            fontWeight: 800,
                            letterSpacing: "0.045em",
                            textTransform: "uppercase",
                            display: "block",
                            fontSize: "10px",
                        }}
                    >
                        Filter by status
                    </Typography>

                    {/* Bulk-select shortcuts. "Show all" opens the door to every
                        status including terminals (FAILED / PUBLISHED / CANCELLED
                        / …) so an admin can navigate to any historic upload for
                        session-replay + diagnostics. "Active only" snaps back to
                        the default live view. */}
                    <Stack direction="row" spacing={0.75}>
                        <Button
                            size="small"
                            onClick={onSelectAllStatuses}
                            data-testid="status-show-all"
                            sx={{
                                color: "#f7fafa",
                                fontSize: "10.5px",
                                textTransform: "none",
                                minWidth: 0,
                                px: 1,
                                py: 0.25,
                                border: "1px solid rgba(255,255,255,0.14)",
                                "&:hover": { background: "rgba(255,255,255,0.08)" },
                            }}
                        >
                            Show all
                        </Button>
                        <Button
                            size="small"
                            onClick={onResetToActive}
                            data-testid="status-active-only"
                            sx={{
                                color: "#f7fafaa6",
                                fontSize: "10.5px",
                                textTransform: "none",
                                minWidth: 0,
                                px: 1,
                                py: 0.25,
                                border: "1px solid rgba(255,255,255,0.14)",
                                "&:hover": { background: "rgba(255,255,255,0.08)" },
                            }}
                        >
                            Active only
                        </Button>
                    </Stack>
                </Stack>

                <StatusChipRow
                    label="Live"
                    statuses={activeStatuses}
                    statusFilter={statusFilter}
                    aggregates={aggregates}
                    statusColors={statusColors}
                    toggleStatusFilter={toggleStatusFilter}
                    chipBg={chipBg}
                    chipBorder={chipBorder}
                    theme={theme}
                />

                <StatusChipRow
                    label="Terminal"
                    statuses={terminalStatuses}
                    statusFilter={statusFilter}
                    aggregates={aggregates}
                    statusColors={statusColors}
                    toggleStatusFilter={toggleStatusFilter}
                    chipBg={chipBg}
                    chipBorder={chipBorder}
                    theme={theme}
                    sx={{ mt: 1 }}
                />
            </Box>

            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: {
                        xs: "1fr",
                        sm: "1fr 1fr",
                        lg: "1fr 1fr 1fr 1fr",
                    },
                    gap: { xs: 1, sm: 1.25 },
                    width: { xs: "100%", lg: "auto" },
                    flexShrink: 0,
                    alignItems: "end",
                }}
            >
                <TextField
                    size="small"
                    label="Contributor"
                    placeholder="ID or name"
                    value={contributorQuery}
                    onChange={(e) => {
                        setPage(0);
                        setContributorQuery(e.target.value);
                    }}
                    inputProps={{ "data-testid": "admin-filter-contributor" }}
                    sx={searchFieldSx(theme)}
                />
                <TextField
                    size="small"
                    label="Package"
                    placeholder="Name or #directory"
                    value={packageNameQuery}
                    onChange={(e) => {
                        setPage(0);
                        setPackageNameQuery(e.target.value);
                    }}
                    inputProps={{ "data-testid": "admin-filter-package" }}
                    sx={searchFieldSx(theme)}
                />
                <TextField
                    size="small"
                    label="From"
                    type="datetime-local"
                    value={fromDate}
                    onChange={(e) => {
                        setPage(0);
                        setFromDate(e.target.value);
                    }}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ "data-testid": "admin-filter-from" }}
                    sx={searchFieldSx(theme)}
                />
                <TextField
                    size="small"
                    label="To"
                    type="datetime-local"
                    value={toDate}
                    onChange={(e) => {
                        setPage(0);
                        setToDate(e.target.value);
                    }}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ "data-testid": "admin-filter-to" }}
                    sx={searchFieldSx(theme)}
                />
                {hasActiveFilters && (
                    <Button
                        size="small"
                        onClick={onClearFilters}
                        data-testid="admin-filter-clear"
                        sx={{
                            // Full-width on every breakpoint — grid column
                            // "1 / -1" spans the whole row regardless of
                            // whether the grid is 1, 2, or 4 columns wide.
                            // Previously xs stuck to column 1 only and read
                            // as a stray narrow button on iPhone.
                            gridColumn: "1 / -1",
                            color: "#f7fafaa6",
                            // iOS 44 px touch target minimum. The tiny 11 px
                            // label used to make this button unreliable to tap.
                            minHeight: { xs: 44, sm: 32 },
                            fontSize: { xs: "13px", sm: "11px" },
                            textTransform: "none",
                            justifySelf: "stretch",
                            border: "1px solid rgba(255,255,255,0.14)",
                            "&:hover": { background: "rgba(255,255,255,0.08)" },
                        }}
                    >
                        Clear search
                    </Button>
                )}
            </Box>
        </Stack>
    </Box>
);

/**
 * One row of togglable status chips. Extracted so the FiltersPanel can render
 * the "Live" and "Terminal" groups separately with a small caption on the left
 * and a horizontally-wrapping / scrolling chip strip on the right.
 */
const StatusChipRow = ({
    label,
    statuses,
    statusFilter,
    aggregates,
    statusColors,
    toggleStatusFilter,
    chipBg,
    chipBorder,
    theme,
    sx,
}) => (
    <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={{ xs: 0.5, sm: 1.25 }}
        sx={sx}
    >
        <Typography
            sx={{
                color: "#f7fafa66",
                fontWeight: 800,
                fontSize: "9.5px",
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                minWidth: { sm: 60 },
                flexShrink: 0,
            }}
        >
            {label}
        </Typography>

        <Stack
            direction="row"
            spacing={1}
            sx={{
                flexWrap: { xs: "nowrap", md: "wrap" },
                gap: { xs: 1, md: 1.15 },
                overflowX: { xs: "auto", md: "visible" },
                overflowY: "hidden",
                pb: { xs: 0.35, md: 0 },
                mx: { xs: -0.25, md: 0 },
                px: { xs: 0.25, md: 0 },
                // iOS Safari won't accept horizontal touch drags inside a
                // vertically-scrolling parent unless the child explicitly
                // opts into horizontal panning. Without touch-action: pan-x
                // the browser interprets every swipe as vertical (because
                // AppScroll owns vertical scroll) and the chip row is
                // "unscrollable" on iPhone even though `overflow-x: auto`
                // sets up the scroll rail correctly. Desktop keeps
                // touch-action: auto so trackpad + mouse-wheel still work.
                touchAction: { xs: "pan-x", md: "auto" },
                // Legacy iOS Safari (< 13) needs this for momentum-style
                // scrolling on any overflow-x: auto container; harmless on
                // newer iOS + Android + desktop.
                WebkitOverflowScrolling: "touch",
                // On xs the parent StatusChipRow Stack is direction:column,
                // which means `flex: 1` on this row grows on the *vertical*
                // axis and horizontal width is only implicit-stretch. Add an
                // explicit width so overflow-x actually has bounds to
                // measure against and the scroll rail activates on narrow
                // widths.
                width: { xs: "100%", md: "auto" },
                minWidth: 0,
                flex: 1,
                ...hiddenScrollbarSx,
            }}
        >
            {statuses.map((status) => {
                const selected = statusFilter.includes(status);
                const count = aggregates?.byStatusCount?.[status];
                return (
                    <Chip
                        key={status}
                        size="small"
                        onClick={() => toggleStatusFilter(status)}
                        data-testid={`status-chip-${status}`}
                        label={
                            <Stack direction="row" spacing={0.75} alignItems="center">
                                <Box
                                    sx={{
                                        width: 6,
                                        height: 6,
                                        borderRadius: "50%",
                                        background: statusColors[status],
                                        flexShrink: 0,
                                    }}
                                />
                                <Box component="span">{STATUS_LABEL[status] || status}</Box>
                                {count != null && (
                                    <Box
                                        component="span"
                                        sx={{
                                            color: "#f7fafaa6",
                                            fontWeight: 800,
                                            fontSize: "11px",
                                        }}
                                    >
                                        {count}
                                    </Box>
                                )}
                            </Stack>
                        }
                        sx={{
                            flexShrink: 0,
                            background: selected ? "rgba(93,98,182,0.24)" : chipBg,
                            border: `1px solid ${selected ? theme.palette.primary.main : chipBorder}`,
                            color: "#f7fafa",
                            height: 31,
                            px: 0.25,
                            "& .MuiChip-label": { px: 1.05 },
                            "&:hover": {
                                background: selected ? "rgba(93,98,182,0.32)" : "rgba(255,255,255,0.1)",
                            },
                        }}
                    />
                );
            })}
        </Stack>
    </Stack>
);

const DesktopUploadsTable = ({ items, loading, chipBg, chipBorder, statusColors, onOpenDiagnostics }) => (
    <Box
        sx={{
            border: `1px solid ${chipBorder}`,
            borderRadius: "16px",
            overflow: "hidden",
            background: "rgba(20,27,40,0.74)",
            backdropFilter: "blur(10px)",
            minWidth: 0,
        }}
    >
        <TableContainer sx={{ width: "100%", overflowX: "auto" }}>
            <Table size="small" sx={{ minWidth: 1120 }}>
                <TableHead>
                    <TableRow>
                        <HeadCell>User</HeadCell>
                        <HeadCell>Package</HeadCell>
                        <HeadCell>Status</HeadCell>
                        <HeadCell>Health</HeadCell>
                        <HeadCell>Progress</HeadCell>
                        <HeadCell align="right">Files</HeadCell>
                        <HeadCell align="right">Throughput</HeadCell>
                        <HeadCell align="right">Started</HeadCell>
                        <HeadCell align="right">Updated</HeadCell>
                        <HeadCell align="right">Diagnose</HeadCell>
                    </TableRow>
                </TableHead>

                <TableBody>
                    {loading && items.length === 0 && (
                        <TableRow>
                            <BodyCell colSpan={10} align="center" sx={{ py: 4.5 }}>
                                <CircularProgress size={20} sx={{ color: "#f7fafa" }} />
                            </BodyCell>
                        </TableRow>
                    )}

                    {!loading && items.length === 0 && (
                        <TableRow>
                            <BodyCell colSpan={10} align="center" sx={{ py: 4.5, color: "#f7fafa80" }}>
                                No active uploads.
                            </BodyCell>
                        </TableRow>
                    )}

                    {items.map((row) => (
                        <TableRow
                            key={row.uploadId}
                            hover
                            sx={{
                                "&:hover": {
                                    background: "rgba(255,255,255,0.035)",
                                },
                            }}
                        >
                            <BodyCell>
                                <Stack spacing={0.45}>
                                    <Box sx={{ color: "#f7fafa", fontWeight: 700 }}>
                                        {row.contributorName || row.contributorId || "—"}
                                    </Box>

                                    {row.contributorName && (
                                        <Box sx={{ color: "#f7fafa66", fontSize: "11px" }}>
                                            {row.contributorId}
                                        </Box>
                                    )}
                                </Stack>
                            </BodyCell>

                            <BodyCell>
                                <Box
                                    sx={{
                                        color: "#f7fafa",
                                        maxWidth: 280,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                        fontWeight: 600,
                                    }}
                                >
                                    {row.packageName || "—"}
                                </Box>

                                <Box
                                    sx={{
                                        color: "#f7fafa66",
                                        fontSize: "11px",
                                        maxWidth: 280,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                        mt: 0.35,
                                    }}
                                >
                                    {row.directoryName || "—"}
                                </Box>
                            </BodyCell>

                            <BodyCell>
                                <Stack spacing={0.35}>
                                    <StatusChip
                                        status={row.status}
                                        chipBg={chipBg}
                                        chipBorder={chipBorder}
                                        statusColors={statusColors}
                                    />
                                    {row.pauseReason && (
                                        <Box
                                            sx={{
                                                color: "#f7fafa66",
                                                fontSize: "10.5px",
                                                letterSpacing: "0.02em",
                                            }}
                                            data-testid={`pause-reason-${row.uploadId}`}
                                        >
                                            {row.pauseReason === "USER" && "user pause"}
                                            {row.pauseReason === "STALL" && "sweeper auto-pause"}
                                            {row.pauseReason === "WATCHDOG" && "watchdog abort"}
                                            {row.pauseReason === "UNKNOWN" && "unknown reason"}
                                        </Box>
                                    )}
                                    {(row.watchdogAutoRestartCount > 0 || row.watchdogGaveUp) && (
                                        <Box
                                            sx={{
                                                color: row.watchdogGaveUp ? "#fca5a5" : "#fcd34d",
                                                fontSize: "10.5px",
                                                fontWeight: 700,
                                                letterSpacing: "0.02em",
                                            }}
                                            data-testid={`watchdog-badge-${row.uploadId}`}
                                            title={
                                                row.watchdogGaveUp
                                                    ? "Client watchdog gave up (restart budget exhausted)"
                                                    : `Client watchdog fired ${row.watchdogAutoRestartCount} auto-restart(s)`
                                            }
                                        >
                                            {row.watchdogGaveUp
                                                ? "⚠ watchdog gave up"
                                                : `⟲ ${row.watchdogAutoRestartCount} restart${row.watchdogAutoRestartCount === 1 ? "" : "s"}`}
                                        </Box>
                                    )}
                                </Stack>
                            </BodyCell>

                            <BodyCell>
                                <HealthCell row={row} />
                            </BodyCell>

                            <BodyCell sx={{ minWidth: 220 }}>
                                <UploadProgress row={row} />
                            </BodyCell>

                            <BodyCell align="right">
                                <Box sx={{ color: "#f7fafa", fontWeight: 700 }}>
                                    {row.filesUploadedCount ?? 0}/{row.expectedFilesCount ?? 0}
                                </Box>
                            </BodyCell>

                            <BodyCell align="right" sx={{ color: "#f7fafaa6", fontSize: "12px" }}>
                                <Stack spacing={0.15} alignItems="flex-end">
                                    <span>
                                        {row.throughputBps > 0
                                            ? `${formatBytes(row.throughputBps)}/s`
                                            : "—"}
                                    </span>
                                    {/* Median across per-PATCH server samples. Rendered under the
                                        client-side rolling throughput when available so admins can
                                        eyeball drift between "what user's browser reports" and
                                        "what server actually observed while bytes were flowing." */}
                                    {row.medianUploadSpeedBytesPerSecond > 0 && (
                                        <span
                                            style={{
                                                color: "#f7fafa66",
                                                fontSize: "10.5px",
                                            }}
                                            title="Server-side median PATCH throughput"
                                        >
                                            med {formatBytes(row.medianUploadSpeedBytesPerSecond)}/s
                                        </span>
                                    )}
                                </Stack>
                            </BodyCell>

                            <BodyCell align="right" sx={{ color: "#f7fafaa6", fontSize: "12px" }}>
                                <Tooltip title={formatTimestamp(row.startUploadTime)}>
                                    <span>{formatRelative(row.startUploadTime)}</span>
                                </Tooltip>
                            </BodyCell>

                            <BodyCell align="right" sx={{ color: "#f7fafaa6", fontSize: "12px" }}>
                                <Tooltip title={formatTimestamp(row.updatedAt)}>
                                    <span>{formatRelative(row.updatedAt)}</span>
                                </Tooltip>
                            </BodyCell>

                            <BodyCell align="right">
                                <Tooltip title="Open diagnostics drawer">
                                    <IconButton
                                        size="small"
                                        aria-label={`open diagnostics for ${row.uploadId}`}
                                        data-testid={`diagnostics-open-${row.uploadId}`}
                                        onClick={() => onOpenDiagnostics(row.uploadId)}
                                        sx={{
                                            color: "#f7fafa",
                                            border: "1px solid rgba(255,255,255,0.14)",
                                            "&:hover": { background: "rgba(255,255,255,0.08)" },
                                        }}
                                    >
                                        <BugReportOutlinedIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </BodyCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    </Box>
);

const HealthCell = ({ row }) => {
    const health = deriveRowHealth(row);
    return (
        <Chip
            size="small"
            label={HEALTH_LABEL[health]}
            color={HEALTH_SEVERITY[health] || "default"}
            variant="filled"
            data-testid={`health-${row.uploadId}`}
            sx={{ fontSize: 10.5, height: 22 }}
        />
    );
};

const MobileUploadsList = ({ items, loading, chipBg, chipBorder, statusColors, onOpenDiagnostics }) => {
    if (loading && items.length === 0) {
        return (
            <Box
                sx={{
                    border: `1px solid ${chipBorder}`,
                    borderRadius: "16px",
                    background: "rgba(20,27,40,0.74)",
                    py: 4.5,
                    display: "flex",
                    justifyContent: "center",
                }}
            >
                <CircularProgress size={22} sx={{ color: "#f7fafa" }} />
            </Box>
        );
    }

    if (!loading && items.length === 0) {
        return (
            <Box
                sx={{
                    border: `1px solid ${chipBorder}`,
                    borderRadius: "16px",
                    background: "rgba(20,27,40,0.74)",
                    py: 4.5,
                    px: 2,
                    color: "#f7fafa80",
                    textAlign: "center",
                    fontSize: "13px",
                }}
            >
                No active uploads.
            </Box>
        );
    }

    return (
        <Stack spacing={1.6}>
            {items.map((row) => (
                <MobileUploadCard
                    key={row.uploadId}
                    row={row}
                    chipBg={chipBg}
                    chipBorder={chipBorder}
                    statusColors={statusColors}
                    onOpenDiagnostics={onOpenDiagnostics}
                />
            ))}
        </Stack>
    );
};

const MobileUploadCard = ({ row, chipBg, chipBorder, statusColors, onOpenDiagnostics }) => (
    <Box
        sx={{
            border: `1px solid ${chipBorder}`,
            borderRadius: "16px",
            background: "rgba(20,27,40,0.78)",
            backdropFilter: "blur(10px)",
            p: 1.7,
            minWidth: 0,
        }}
    >
        <Stack spacing={1.65}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1.5}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                        sx={{
                            color: "#f7fafa",
                            fontWeight: 800,
                            fontSize: "14px",
                            lineHeight: 1.3,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {row.contributorName || row.contributorId || "—"}
                    </Typography>

                    {row.contributorName && (
                        <Typography
                            sx={{
                                color: "#f7fafa66",
                                fontSize: "11px",
                                mt: 0.35,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {row.contributorId}
                        </Typography>
                    )}
                </Box>

                <StatusChip
                    status={row.status}
                    chipBg={chipBg}
                    chipBorder={chipBorder}
                    statusColors={statusColors}
                />
            </Stack>

            <Box
                sx={{
                    borderTop: "1px solid rgba(255,255,255,0.07)",
                    pt: 1.35,
                    minWidth: 0,
                }}
            >
                <Typography
                    sx={{
                        color: "#f7fafa",
                        fontWeight: 700,
                        fontSize: "13px",
                        lineHeight: 1.3,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                    }}
                >
                    {row.packageName || "—"}
                </Typography>

                <Typography
                    sx={{
                        color: "#f7fafa66",
                        fontSize: "11px",
                        mt: 0.35,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                    }}
                >
                    {row.directoryName || "—"}
                </Typography>
            </Box>

            <UploadProgress row={row} />

            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                    gap: 1,
                }}
            >
                <InfoPair label="Files" value={`${row.filesUploadedCount ?? 0}/${row.expectedFilesCount ?? 0}`} />
                <InfoPair label="Speed" value={row.throughputBps > 0 ? `${formatBytes(row.throughputBps)}/s` : "—"} />
                <InfoPair label="Started" value={formatRelative(row.startUploadTime)} title={formatTimestamp(row.startUploadTime)} />
                <InfoPair label="Updated" value={formatRelative(row.updatedAt)} title={formatTimestamp(row.updatedAt)} />
            </Box>

            <Stack direction="row" spacing={1} alignItems="center" sx={{ pt: 0.5 }}>
                <HealthCell row={row} />
                <Box sx={{ flexGrow: 1 }} />
                <IconButton
                    size="small"
                    aria-label={`open diagnostics for ${row.uploadId}`}
                    data-testid={`diagnostics-open-mobile-${row.uploadId}`}
                    onClick={() => onOpenDiagnostics(row.uploadId)}
                    sx={{
                        color: "#f7fafa",
                        border: "1px solid rgba(255,255,255,0.14)",
                    }}
                >
                    <BugReportOutlinedIcon fontSize="small" />
                </IconButton>
            </Stack>
        </Stack>
    </Box>
);

const UploadProgress = ({ row }) => (
    <Stack spacing={0.85} sx={{ minWidth: 0 }}>
        <LinearProgress
            variant={row.percentComplete == null ? "indeterminate" : "determinate"}
            value={row.percentComplete ?? 0}
            sx={{
                height: 7,
                borderRadius: 999,
                background: "rgba(255,255,255,0.08)",
                "& .MuiLinearProgress-bar": {
                    borderRadius: 999,
                },
            }}
        />

        <Box
            sx={{
                color: "#f7fafaa6",
                fontSize: "11px",
                lineHeight: 1.4,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
            }}
        >
            {row.percentComplete != null ? `${row.percentComplete}%` : "—"} · {formatBytes(row.bytesUploaded)}
            {row.totalSize ? ` of ${formatBytes(row.totalSize)}` : ""}
        </Box>
    </Stack>
);

const StatusChip = ({ status, chipBg, chipBorder, statusColors }) => (
    <Chip
        size="small"
        label={STATUS_LABEL[status] || status}
        sx={{
            background: chipBg,
            border: `1px solid ${statusColors[status] || chipBorder}`,
            color: "#f7fafa",
            fontSize: "11px",
            height: "24px",
            maxWidth: { xs: 132, sm: 150 },
            flexShrink: 0,
            "& .MuiChip-label": {
                overflow: "hidden",
                textOverflow: "ellipsis",
                px: 1,
            },
        }}
    />
);

const InfoPair = ({ label, value, title }) => {
    const content = (
        <Box
            sx={{
                border: "1px solid rgba(255,255,255,0.08)",
                borderRadius: "11px",
                background: "rgba(255,255,255,0.035)",
                px: 1.1,
                py: 1,
                minWidth: 0,
            }}
        >
            <Typography
                sx={{
                    color: "#f7fafa66",
                    fontSize: "9.5px",
                    lineHeight: 1.1,
                    fontWeight: 800,
                    letterSpacing: "0.045em",
                    textTransform: "uppercase",
                    mb: 0.5,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                }}
            >
                {label}
            </Typography>

            <Typography
                sx={{
                    color: "#f7fafa",
                    fontSize: "12px",
                    fontWeight: 700,
                    lineHeight: 1.25,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                }}
            >
                {value}
            </Typography>
        </Box>
    );

    return title ? <Tooltip title={title}>{content}</Tooltip> : content;
};

const CounterTile = ({ label, value, hint, background }) => (
    <Tooltip title={hint || ""} arrow disableHoverListener={!hint}>
        <Box
            sx={{
                width: "100%",
                minWidth: 0,
                minHeight: { xs: 78, sm: 86 },
                px: { xs: 1.5, sm: 1.9 },
                py: { xs: 1.3, sm: 1.65 },
                borderRadius: { xs: "12px", sm: "14px" },
                background,
                border: "1px solid rgba(255,255,255,0.09)",
                backdropFilter: "blur(10px)",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                overflow: "hidden",
                boxSizing: "border-box",
            }}
        >
            <Box
                sx={{
                    color: "#f7fafaa6",
                    fontSize: { xs: "9px", sm: "10px" },
                    lineHeight: 1.15,
                    fontWeight: 800,
                    letterSpacing: "0.045em",
                    textTransform: "uppercase",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                }}
            >
                {label}
            </Box>

            <Box
                sx={{
                    color: "#fff",
                    fontSize: { xs: "17px", sm: "20px" },
                    fontWeight: 850,
                    lineHeight: 1.05,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    mt: 1,
                }}
            >
                {value}
            </Box>
        </Box>
    </Tooltip>
);

const HeadCell = ({ children, sx, ...rest }) => (
    <TableCell
        {...rest}
        sx={{
            color: "#f7fafaa6",
            fontSize: "10px",
            fontWeight: 800,
            letterSpacing: "0.045em",
            textTransform: "uppercase",
            borderBottom: "1px solid rgba(255,255,255,0.12)",
            background: "rgba(255,255,255,0.03)",
            whiteSpace: "nowrap",
            py: 1.4,
            px: 1.75,
            ...sx,
        }}
    >
        {children}
    </TableCell>
);

const BodyCell = ({ children, sx, ...rest }) => (
    <TableCell
        {...rest}
        sx={{
            color: "#f7fafa",
            fontSize: "13px",
            borderBottom: "1px solid rgba(255,255,255,0.06)",
            verticalAlign: "middle",
            py: 1.55,
            px: 1.75,
            ...sx,
        }}
    >
        {children}
    </TableCell>
);

export default AdminUploadsLivePage;